import React, { PropsWithChildren, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import axios from 'axios';
import { IBusinessToolSections } from 'constants/businessTool';
import { IUnitedSections } from 'constants/businessToolSections';
import { merge } from 'lodash';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { BusinessPlanAIApi, isProgrammaticAIRequestAbort } from 'services/businessPlanAI/businessPlanAI.api';
import { aiAllDocumentFields } from 'services/businessPlanAI/sections';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getBusinessPlanType } from 'store/useBusinessToolStore/selectors';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import { useBusinessPlanAIFieldUpdate } from 'hooks/useBusinessPlanAIFieldUpdate';

import { SUBSCRIPTION_PLAN } from 'interfaces/subscriptions';

import { useModalContext } from 'components/ModalManager';

import { getValidatedMetadata } from 'helpers/metadataParser';

import BusinessToolAiContext from './business-tool-ai-context';
import { AICancelConfirmModal } from '../AICancelConfirmModal';
import { useControllerManagement } from './hooks/useControllerManagement';
import { getDefaultLoadingStates } from './utils';
import { AIGenerationErrorModalParent } from '../AIGenerationErrorModalParent/AIGenerationErrorModalParent';

export const BusinessToolAiProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { id: businessPlanId = '' } = useParams();
  const { showModal, hideModal } = useModalContext();

  const businessPlanType = useBusinessToolStore(getBusinessPlanType);
  const updateMutation = useUpdateBusinessPlan();
  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const { addController, removeController, abortController, abortAllControllers } = useControllerManagement();
  const { getValues, setValue } = useFormContext();

  const userSubscription = useUserSubscriptionStore(getUserSubscription);
  const isStandardOrLightTariff =
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.STANDARD ||
    userSubscription?.currentSubscription?.subscriptionPlan === SUBSCRIPTION_PLAN.LIGHT;

  const [loadingStates, setLoadingStates] = useState(() => getDefaultLoadingStates(businessPlanType, false));
  const [isWholeDocumentLoading, setIsWholeDocumentLoading] = useState(false);
  const { handleApiRequest } = useBusinessPlanAIFieldUpdate(aiAllDocumentFields[businessPlanType], 20, 'text', false);

  const startSectionAI = (sectionName: IUnitedSections) => {
    setLoadingStates((prev) => ({ ...prev, [sectionName]: true }));
  };

  const finishSectionAI = (sectionKey: IUnitedSections) => {
    setLoadingStates((prev) => ({ ...prev, [sectionKey]: false }));
    removeController(sectionKey);
  };

  const updateFormValues = (sectionKey: IBusinessToolSections, data: unknown) => {
    const currentValues = getValues(sectionKey);
    const updatedValues = merge(currentValues, data);
    setValue(sectionKey, updatedValues);
  };

  const cancelSectionAI = ({
    sectionKey,
    onConfirmCallback,
    onAbortCallback,
  }: {
    sectionKey: IUnitedSections;
    onConfirmCallback?: () => void;
    onAbortCallback?: () => void;
  }) => {
    showModal(AICancelConfirmModal, {
      onAbort: () => {
        if (onAbortCallback) {
          onAbortCallback();
        }
        hideModal();
      },
      onConfirm: () => {
        if (onConfirmCallback) {
          onConfirmCallback();
        }
        abortController(sectionKey);
        BusinessPlanAIApi.cancelAllRequest();
      },
    });
  };

  const generateAISectionRequest = async (sectionKey: IUnitedSections, controller: AbortController) => {
    const params = { id: businessPlanId, names: [sectionKey], useCustomModel: false };
    return await BusinessPlanAIApi.generateChapter(params, controller.signal);
  };

  const handleApiError = (error: unknown) => {
    if (isProgrammaticAIRequestAbort(error)) {
      return;
    }

    if (!axios.isCancel(error)) {
      if (axios.isAxiosError(error) && process.env.NODE_ENV === 'development') {
        console.error({
          status: error.response?.status,
          message: error.message,
          path: error.config?.url ? new URL(error.config.url).pathname : undefined,
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.error({
            type: error instanceof Error ? error.name : typeof error,
            message: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      }
      showModal(AIGenerationErrorModalParent, { close: hideModal, errorMessage: 'SOME_ERROR' });
    }
  };

  const generateSectionAI = async (sectionKey: IUnitedSections) => {
    startSectionAI(sectionKey);
    const controller = addController(sectionKey);

    try {
      const aiGeneratedSection = await generateAISectionRequest(sectionKey, controller);
      aiGeneratedSection.map((aiSection) => updateFormValues(aiSection.chapter, JSON.parse(aiSection.message)));
    } catch (error) {
      handleApiError(error);
    } finally {
      finishSectionAI(sectionKey);
    }
  };

  const generateSectionAIImproved = async (sectionKey: IUnitedSections, callback: () => Promise<void>) => {
    startSectionAI(sectionKey);

    try {
      await callback();
    } catch (error) {
      handleApiError(error);
    } finally {
      finishSectionAI(sectionKey);
    }
  };

  const generateWholeDocument = async () => {
    setIsWholeDocumentLoading(true);
    setLoadingStates(getDefaultLoadingStates(businessPlanType, true));

    try {
      await handleApiRequest('generate', true);
      if (!isStandardOrLightTariff) {
        // if FREE tarif, then save in metadata that the whole document was generated
        const params = {
          id: businessPlanId,
          body: {
            ...businessPlan,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              isGeneratedWholeDocument: true,
            }),
          },
        };
        updateMutation.mutateAsync(params);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsWholeDocumentLoading(false);
      setLoadingStates(getDefaultLoadingStates(businessPlanType, false));
    }
  };

  const cancelWholeDocumentAI = (onConfirmCallback?: () => void) => {
    const cancelHandler = () => {
      abortAllControllers();
      BusinessPlanAIApi.cancelAllRequest();
      setLoadingStates(getDefaultLoadingStates(businessPlanType, false));
      setIsWholeDocumentLoading(false);
    };
    showModal(AICancelConfirmModal, {
      onAbort: hideModal,
      onConfirm: () => {
        if (onConfirmCallback) {
          onConfirmCallback();
        }
        cancelHandler();
      },
    });
  };

  const isLoading = Object.values(loadingStates).some(Boolean);

  return (
    <BusinessToolAiContext.Provider
      value={{
        isLoading,
        loadingStates,
        isWholeDocumentLoading,
        generateSectionAI,
        cancelSectionAI,
        generateWholeDocument,
        cancelWholeDocumentAI,
        generateSectionAIImproved,
      }}
    >
      {children}
    </BusinessToolAiContext.Provider>
  );
};
